import { ReactNode } from 'react';
import clsx from 'clsx';
import Lottie from 'react-lottie';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import SearchBar from '~/shared/components/SearchBar';
import FilterScrollableList from '~/shared/components/filters/FilterScrollableList';
import loadingSpinner from '~/shared/assets/lotties/loading-spinner.json';
import Typography from '~/shared/components/Typography';

interface SearchFilterBaseProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  placeholder: string;

  searchResults: GroupedOption[];
  isLoading: boolean;
  showResults: boolean;
  focusedIndex: number;
  flattenedResults: SelectOption[];

  localSelections: SelectOption[];
  onSelect: (option: SelectOption) => void;
  selectedIdSet: Set<string>;

  renderOption: (option: SelectOption, isSelected: boolean, isFocused?: boolean) => ReactNode;

  className?: string;
  mustTypeFirstMessage?: string;
  resultsRef?: React.RefObject<HTMLDivElement>;
  onResultsMouseDown?: () => void;
  onResultsMouseUp?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function SearchFilterBase({
  inputValue,
  onInputChange,
  inputRef,
  placeholder,
  searchResults,
  isLoading,
  showResults,
  focusedIndex,
  flattenedResults,
  localSelections,
  onSelect,
  selectedIdSet,
  renderOption,
  className,
  mustTypeFirstMessage = 'Start typing to search',
  resultsRef,
  onResultsMouseDown,
  onResultsMouseUp,
  onKeyDown,
  onFocus,
  onBlur,
}: SearchFilterBaseProps) {
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(event.target.value);
  };

  return (
    <div
      className={clsx(
        'flex flex-col relative overflow-auto flex-1',
        (searchResults.length === 0 || !showResults) && 'pb-5',
        className
      )}
    >
      {/* Search Input */}
      <div className={clsx('relative z-10 bg-white')}>
        <SearchBar
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          size="md-responsive"
          className="w-full"
          endAdornment={
            isLoading ? (
              <span className="w-fit -mr-3 overflow-hidden inline-block">
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: loadingSpinner,
                    rendererSettings: {
                      preserveAspectRatio: 'xMidYMid meet',
                    },
                  }}
                  height={28}
                  width={56}
                  isClickToPauseDisabled
                />
              </span>
            ) : undefined
          }
        />
      </div>

      {/* Results Dropdown */}
      {(showResults || localSelections.length > 0) && (
        <FilterScrollableList
          ref={resultsRef}
          className="z-20"
          style={{ minWidth: '384px' }}
          onMouseDown={onResultsMouseDown}
          onMouseUp={onResultsMouseUp}
        >
          {searchResults.length > 0 ? (
            <>
              {searchResults.map((group) => (
                <div key={group.label} className="">
                  {group.options.map((option) => {
                    // Find the index of this option in flattenedResults for focus highlighting
                    const optionIndex = flattenedResults.findIndex(item => item.id === option.id);
                    const isFocused = optionIndex === focusedIndex;
                    
                    return (
                      <div 
                        key={option.id} 
                        data-option-index={optionIndex}
                        onClick={() => onSelect(option)}
                      >
                        {renderOption(option, selectedIdSet.has(option.id), isFocused)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          ) : isLoading ? null : (
            <div className="w-full rounded-md bg-white transition duration-200 delay-200 z-40 -mt5">
              <Typography className="p-2 text-center text-gray-300" variant="caption">
                {!inputValue.trim() ? mustTypeFirstMessage : 'No results found'}
              </Typography>
            </div>
          )}
        </FilterScrollableList>
      )}
    </div>
  );
}
